import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  GeneralLedgerPageDocument,
  GeneralLedgerPageQuery,
  LegalEntityLedgerAccountPageFragment,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import JournalEntryDetailSidepanel from 'modules/finance/components/JournalEntryDetailSidepanel'
import JournalEntryDatatable from 'modules/finance/components/JournalsEntry/Datatable'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { ExportSidepanel } from 'modules/universal_exports/components/ExportSidepanel'

const GeneralLedgerPage = () => {
  const [showExportLedgerSidepanel, setShowExportLedgerSidepanel] = useState<boolean>(false)
  const [selectedJournalEntryId, setSelectedJournalEntryId] = useState<number | null>(null)

  const [legalEntity, setLegalEntity] = useState<LegalEntityLedgerAccountPageFragment>()
  const { company, employee } = useCurrentSession()

  const { error, data, loading } = useQuery<GeneralLedgerPageQuery, Record<string, never>>(
    GeneralLedgerPageDocument
  )

  const canManageJournalEntries = data?.finance?._policies?.journalEntriesManage?.can ?? false
  const canManageAccounts = data?.finance?._policies?.accountingManageAccounts?.can ?? false

  const legalEntities = useUnsafeMemo(() => data?.companies?.legalEntities ?? [], [data])

  const legalEntitiesOptions =
    legalEntities.map((le) => ({ label: le.legalName ?? '', value: le.id })) ?? []

  const getDefaultLegalEntityId = useUnsafeMemo(() => {
    if (data && data.companies.legalEntities.length > 0) {
      let preferedLegalEntity = data.companies.legalEntities.find(
        (el) => el.id === employee?.legalEntity?.id
      )
      if (!preferedLegalEntity) {
        preferedLegalEntity = data.companies.legalEntities.find((el) => el.main)
      }
      return preferedLegalEntity?.id
    }
  }, [data, employee?.legalEntity?.id])

  useUnsafeEffect(() => {
    if (getDefaultLegalEntityId) {
      setLegalEntity(legalEntities.find((le) => le.id === getDefaultLegalEntityId))
    }
  }, [getDefaultLegalEntityId, legalEntities, loading])

  const onLegalEntityChange = useUnsafeCallback(
    (value: Record<string, unknown>) => {
      setLegalEntity(legalEntities.find((le) => le.id === value.legalEntityId))
    },
    [legalEntities]
  )

  return (
    <ResponsivePage.Body authorized={canManageJournalEntries} error={error} loading={loading}>
      <Box paddingTop="s32">
        <JournalEntryDatatable
          legalEntityId={legalEntity?.id}
          companyId={company.id}
          onFilterChange={onLegalEntityChange}
          legalEntitiesOptions={legalEntitiesOptions}
          defaultLegalEntityId={getDefaultLegalEntityId}
          onClickDetail={setSelectedJournalEntryId}
          canManageAccounts={canManageAccounts}
          onExportClick={() => {
            track(trackingEvents.accounting.accounting.clickExportGeneralLedger)
            setShowExportLedgerSidepanel(true)
          }}
        />
      </Box>

      {selectedJournalEntryId && (
        <JournalEntryDetailSidepanel
          journalEntryId={selectedJournalEntryId}
          onClose={() => {
            setSelectedJournalEntryId(null)
          }}
        />
      )}

      {showExportLedgerSidepanel && (
        <ExportSidepanel
          resource="finance.journal_line"
          onClose={() => setShowExportLedgerSidepanel(false)}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default GeneralLedgerPage
